import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearGameSessionState } from '@/lib/clear-game-session-state';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { MessageSquare, ChevronDown, ChevronUp, ScrollText } from 'lucide-react';
import { toast } from '@/lib/toast';
import { useLobbyStore } from '@/lib/stores/lobbyStore';
import { useShallow } from 'zustand/react/shallow';
import { useGameStore, DUEL_COACH_PLACEHOLDER_ID } from '@/lib/stores/gameStore';
import { useAuthStore } from '@/lib/stores/authStore';
import { track } from '@/lib/analytics';
import { useAccountModalStore } from '@/lib/stores/accountModalStore';
import { useChatScrollKickStore } from '@/lib/stores/chatScrollKickStore';
import { websocketService } from '@/lib/websocket-service';
import { gameConnectionManager } from '@/lib/game-connection-manager';
import type { VirtuosoHandle } from 'react-virtuoso';
import { ChatRoomTabs } from './chat/ChatRoomTabs';
import { LobbyRoomContent } from './chat/LobbyRoomContent';
import { GameChatContent } from './chat/GameChatContent';
import { JournalTabContent } from './chat/JournalTabContent';
import { ChatMessage } from '@/components/chat/ChatMessage';
import {
  parseSlashCommand,
  defaultInputState,
  type InputState,
  getInputState,
  getCommandsForRoom,
  filterCommands,
  buildInvitePickerRows,
  type InvitePickerRow,
  type SlashCommandDefinition,
} from './chat-commands';
import {
  useGameSlashCommands,
  type GameCommandContext,
  type SlashSelectResult,
} from '@/hooks/useGameSlashCommands';
import { DEFAULT_PLAYER_AVATAR } from '@/lib/media';
import { getNumberedSpells, buildFreeCastContext } from './game-commands';
import { actionTriggerRef } from '@/lib/action-trigger';
import { usePanelCollapse } from '@/lib/hooks/usePanelCollapse';
import { slashMenuItemId } from './chat/slash-menu-utils';
import PoolSpendInputDialog from './chat/PoolSpendInputDialog';
// Import directly from module to avoid pulling in server-only dependencies from barrel export
import { pickChatBubbleColor } from '@danda/shared/constants/chat-colors';
import { getStaffAvatarUrl, getStaffPortraitUrl, getVideoUrl } from '@/lib/media';
import {
  DecisionStatusEnum,
  RoomTypeEnum,
  MIN_PM_BALANCE,
  LOW_BALANCE_GATE_TEXT,
  LOW_BALANCE_GATE_BUTTON_LABEL,
  EQUIPPABLE_ITEM_KINDS,
} from '@danda/shared';
import type { GameCharacterInfo } from '@danda/shared';
import { apiClient } from '@/lib/api';
import { useFeedbackStore } from '@/lib/stores/feedbackStore';
import { usePmBalanceStore } from '@/lib/stores/pmBalanceStore';
import { formatSceneName, getXpForNextLevel } from '@/lib/utils';
import { resolveCardUses as resolveCardUsesFor, type CardUsesContext } from './card-uses';

/** NPC hero video URLs for 1:1 chats (served from GCS) */
const NPC_HERO_VIDEOS: Record<string, string> = {
  tavian: getVideoUrl('tavian.mp4'),
  aldric: getVideoUrl('aldric.mp4'),
  kira: getVideoUrl('kira.mp4'),
  grolt: getVideoUrl('grolt.mp4'),
};

/** Staff NPC avatar asset names, keyed by sender id — used to derive the
 *  corresponding portrait URL via `getStaffPortraitUrl()`. */
const STAFF_PORTRAIT_AVATAR_NAMES: Record<string, string> = {
  tavian: 'tavian-avatar.webp',
  aldric: 'aldric-avatar.webp',
  kira: 'kira-avatar.webp',
  grolt: 'grolt-avatar.webp',
};

export default function ChatPanel() {
  const navigate = useNavigate();
  const [inputStates, setInputStates] = useState<Record<string, InputState>>({});
  const [slashCommandIndex, setSlashCommandIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const lobbyVirtuosoRef = useRef<VirtuosoHandle>(null);
  const gameVirtuosoRef = useRef<VirtuosoHandle>(null);
  const [isExpanded, toggleExpanded] = usePanelCollapse('chat');

  // Ref to always access the latest handleSendMessage from setTimeout callbacks
  // This prevents stale closures when slash command selection triggers a deferred send
  const handleSendMessageRef = useRef<() => void>(() => {});

  // Lobby Store — useShallow so the component re-renders only when the
  // selected fields change, not on every unrelated store update.
  const {
    chatRooms,
    activeChatRoomId,
    switchChatRoom,
    removeChatRoom,
    isOnline: isLobbyOnline,
    parties,
    currentPartyId,
    selectedCharacterId,
    characters,
    lobbyCharacters,
    friends,
    pendingInvites,
    isWaitingForResponse,
    partyDecisions,
    clearPartyDecision,
    lobbyJournal,
    lobbyJournalFetched,
    setLobbyJournal,
    setLobbyJournalLoading,
    setLobbyJournalFetched,
  } = useLobbyStore(
    useShallow((s) => ({
      chatRooms: s.chatRooms,
      activeChatRoomId: s.activeChatRoomId,
      switchChatRoom: s.switchChatRoom,
      removeChatRoom: s.removeChatRoom,
      isOnline: s.isOnline,
      parties: s.parties,
      currentPartyId: s.currentPartyId,
      selectedCharacterId: s.selectedCharacterId,
      characters: s.characters,
      lobbyCharacters: s.lobbyCharacters,
      friends: s.friends,
      pendingInvites: s.pendingInvites,
      isWaitingForResponse: s.isWaitingForResponse,
      partyDecisions: s.partyDecisions,
      clearPartyDecision: s.clearPartyDecision,
      lobbyJournal: s.lobbyJournal,
      lobbyJournalFetched: s.lobbyJournalFetched,
      setLobbyJournal: s.setLobbyJournal,
      setLobbyJournalLoading: s.setLobbyJournalLoading,
      setLobbyJournalFetched: s.setLobbyJournalFetched,
    }))
  );
  // Derive activeRoom from already-subscribed chatRooms + activeChatRoomId
  // instead of the dedicated useActiveChatRoom() hook. The hook is a separate
  // useLobbyStore subscription that returns a `.find()` result — every message
  // arrival creates a new chatRooms array → new object ref → forces an extra
  // ChatPanel re-render on top of the shallow selector above.
  const activeRoom = useMemo(
    () => chatRooms.find((r) => r.id === activeChatRoomId) ?? null,
    [chatRooms, activeChatRoomId]
  );

  // Game Store — useShallow so re-renders track only the selected fields.
  const {
    connectionStatus,
    messages: gameMessages,
    gameSessionId,
    moduleTitle,
    currentScene,
    partyMembers: gamePartyMembers,
    sceneCharacters: gameSceneCharacters,
    playerCharacterId: gamePlayerCharacterId,
    sceneItems: gameSceneItems,
    sceneInteractables: gameSceneInteractables,
    travelDestinations: gameTravelDestinations,
    encounterType,
    unreadJournalCount,
    clearJournalUnread,
  } = useGameStore(
    useShallow((s) => ({
      connectionStatus: s.connectionStatus,
      messages: s.messages,
      gameSessionId: s.gameSessionId,
      moduleTitle: s.moduleTitle,
      currentScene: s.currentScene,
      partyMembers: s.partyMembers,
      sceneCharacters: s.sceneCharacters,
      playerCharacterId: s.playerCharacterId,
      sceneItems: s.sceneItems,
      sceneInteractables: s.sceneInteractables,
      travelDestinations: s.travelDestinations,
      encounterType: s.encounterType,
      unreadJournalCount: s.unreadJournalCount,
      clearJournalUnread: s.clearJournalUnread,
    }))
  );
  const isGameConnected = connectionStatus.status === 'connected';
  const [gameTab, setGameTab] = useState<'game' | 'journal'>('game');
  const [lobbyTab, setLobbyTab] = useState<'chat' | 'journal'>('chat');

  // Player character data for game slash commands
  const playerChar = useMemo(
    () =>
      gamePlayerCharacterId
        ? gamePartyMembers.find((c) => c.charId === gamePlayerCharacterId)
        : undefined,
    [gamePlayerCharacterId, gamePartyMembers]
  );

  // Innate spell free-cast quota context (species / Magic Initiate). Used by
  // both `useGameSlashCommands` (/cast menu) and the Ctrl+N shortcut list so
  // exhausted innate spells disappear from the menu consistently with the
  // Actions panel. Built lazily so the memo only recomputes when the underlying
  // session/permanent fields change.
  const freeCastContext = useMemo(
    () => buildFreeCastContext(playerChar?.permanent, playerChar?.session),
    [playerChar?.permanent, playerChar?.session]
  );

  // Per-card pool-remaining for pool-spend abilities (Paladin Lay on Hands).
  // The click path in CharDetailsPanel computes this inline via
  // `resolveCardUses`; the typed-slash path needs the same data injected into
  // GameCommandContext so `getAbilityCommands` can pre-populate
  // `meta.poolRemaining` when building each card's slash command. Without
  // this, typed `/lay-on-hands` renders an empty pool picker and fires
  // `pool_spend: None` (engine defaults to 1 HP, useless heal).
  const cardPoolRemaining = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    const cards = playerChar?.permanent?.action_cards ?? [];
    const classAbilities = (playerChar?.permanent?.class_abilities ?? {}) as Record<
      string,
      unknown
    >;
    const classResources = (playerChar?.session?.class_resources ?? {}) as Record<string, unknown>;
    const numFromMap = (obj: Record<string, unknown>, key: string): number => {
      const v = obj[key];
      return typeof v === 'number' ? v : 0;
    };
    for (const card of cards) {
      const req = card.availability?.resource_requirement;
      if (req?.resource === 'lay_on_hands_pool') {
        const max = numFromMap(classAbilities, 'max_lay_on_hands_pool');
        const spent = numFromMap(classResources, 'lay_on_hands_pool');
        map[card.id] = Math.max(0, max - spent);
      }
    }
    return map;
  }, [
    playerChar?.permanent?.action_cards,
    playerChar?.permanent?.class_abilities,
    playerChar?.session?.class_resources,
  ]);

  // Cards whose resource is spent, so the slash menu can hide them the way the
  // Actions panel greys them out. Resolved through `resolveCardUses` — the same
  // resolver the click path uses — so the two views can't disagree about what
  // is castable. `null` from the resolver means "not tracked here", which stays
  // visible and lets the backend have the final say.
  const spentCardIds = useMemo<ReadonlySet<string>>(() => {
    const spent = new Set<string>();
    const cards = playerChar?.permanent?.action_cards ?? [];
    const ctx: CardUsesContext = {
      classAbilities: (playerChar?.permanent?.class_abilities ?? {}) as Record<string, unknown>,
      classResources: (playerChar?.session?.class_resources ?? {}) as Record<string, unknown>,
      featureUses: (playerChar?.session?.feature_uses ?? {}) as Record<string, number>,
      level: playerChar?.permanent?.level,
      // Load-bearing for Bardic Inspiration: its max is the CHA modifier, read
      // from here. Without it `chaMod` is 0, `max` collapses to 1, and the card
      // vanishes from the menu after one use while the Actions panel still
      // shows 2/3 — the exact drift this gate exists to remove.
      computed: playerChar?.permanent?.computed as Record<string, unknown> | undefined,
    };
    for (const card of cards) {
      const req = card.availability?.resource_requirement;
      if (!req) continue;
      const resolved = resolveCardUsesFor(ctx, req.resource, card.label, req.amount);
      if (!resolved) continue;
      // An unresolvable max reads as 0 here (`abilityNum` returns 0 for a key
      // `class_abilities` never published), which is NOT the same as "none
      // left". Gating on it would hide a perfectly usable card. Mobile's
      // `_maxForResource` returns null for exactly this and leaves the card up
      // so the backend has the final say; match that.
      if (resolved.max <= 0) continue;
      // Compare against the card's COST, not zero: Heightened and Quickened
      // Spell cost 2 sorcery points, so one point left is not enough and the
      // backend rejects it. `feature_use` is the carve-out — there `amount` is
      // the per-rest cap and each use costs exactly 1, which is why mobile's
      // gate special-cases it the same way.
      const cost = req.resource === 'feature_use' ? 1 : Math.max(1, req.amount);
      if (resolved.remaining < cost) spent.add(card.id);
    }
    return spent;
  }, [
    playerChar?.permanent?.action_cards,
    playerChar?.permanent?.class_abilities,
    playerChar?.permanent?.computed,
    playerChar?.permanent?.level,
    playerChar?.session?.class_resources,
    playerChar?.session?.feature_uses,
  ]);

  // Game slash command context — memoized to avoid re-creating on every render
  const gameCommandCtx = useMemo(
    (): GameCommandContext => ({
      inCombat: encounterType === 'combat',
      partyMembers: gamePartyMembers,
      sceneCharacters: gameSceneCharacters,
      playerCharacterId: gamePlayerCharacterId,
      sceneItems: gameSceneItems,
      sceneInteractables: gameSceneInteractables,
      spellcasting: playerChar?.spellcasting,
      spellSlotsUsed: playerChar?.spellSlotsUsed,
      spellMechanics: playerChar?.permanent?.spell_mechanics,
      preparedSpells: playerChar?.preparedSpells,
      equipment: playerChar?.equipment,
      inventory: (playerChar?.permanent?.inventory ?? []) as Array<{
        name: string;
        item_id?: string;
        item_kind?: string;
        quantity?: number;
      }>,
      // Slash-command autocomplete reads action_cards (single SoT, same as
      // the Actions panel). ability_mechanics is backend-internal only.
      actionCards: playerChar?.permanent?.action_cards,
      travelDestinations: gameTravelDestinations,
      freeCastContext,
      playerConcentration: playerChar?.concentrationSpell ?? undefined,
      cardPoolRemaining,
      spentCardIds,
      // Class/level/features for class-gated slash commands (e.g.
      // /font-of-inspiration). Backend handler is authoritative; these
      // power the FE visibility filter only.
      playerClass: playerChar?.permanent?.class,
      playerLevel: playerChar?.permanent?.level,
      playerClassFeatures: playerChar?.permanent?.class_features,
    }),
    [
      encounterType,
      gamePartyMembers,
      gameSceneCharacters,
      gamePlayerCharacterId,
      gameSceneItems,
      gameSceneInteractables,
      gameTravelDestinations,
      playerChar?.spellcasting,
      playerChar?.spellSlotsUsed,
      playerChar?.permanent?.spell_mechanics,
      playerChar?.preparedSpells,
      playerChar?.equipment,
      playerChar?.permanent?.inventory,
      playerChar?.permanent?.action_cards,
      freeCastContext,
      playerChar?.concentrationSpell,
      cardPoolRemaining,
      spentCardIds,
      playerChar?.permanent?.class,
      playerChar?.permanent?.level,
      playerChar?.permanent?.class_features,
    ]
  );

  // Game slash command state machine
  const gameSlash = useGameSlashCommands(gameCommandCtx);
  // Destructure stable callbacks to avoid re-creating useCallbacks/useEffects every render
  // (the gameSlash object itself is a new ref each render, but individual useCallback values are stable)
  const {
    triggerSpellShortcut,
    triggerWeaponAttackShortcut,
    triggerAbilityShortcut,
    triggerItemShortcut,
    triggerCardShortcut,
    handleSelect: gameSlashHandleSelect,
    handleBack: gameSlashHandleBack,
    handleTextChange: gameSlashHandleTextChange,
    reset: gameSlashReset,
    finalizeForSubmit: gameSlashFinalizeForSubmit,
    setSelectedIndex: gameSlashSetSelectedIndex,
  } = gameSlash;
  // Keep `finalizeForSubmit` in a ref so handleSendMessage always reads the
  // latest closure (the function captures `stage` + `lastBuildResultRef` and
  // recreates on every state change).
  const finalizeForSubmitRef = useRef(gameSlashFinalizeForSubmit);
  finalizeForSubmitRef.current = gameSlashFinalizeForSubmit;

  // Numbered spell list for Ctrl+N shortcuts (cantrips first, then by level).
  // `getNumberedSpells` intentionally returns exhausted innate spells (so the
  // Actions panel can render them as disabled) — we filter them out here so
  // Ctrl+N doesn't trigger a cast the runtime will reject.
  const numberedSpells = useMemo(
    () =>
      getNumberedSpells(
        playerChar?.spellcasting,
        playerChar?.spellSlotsUsed,
        playerChar?.permanent?.spell_mechanics,
        playerChar?.preparedSpells,
        freeCastContext
      ).filter((s) => {
        if (s.isCantrip) return true;
        if (s.freeCastable) return s.freeCastRemaining == null || s.freeCastRemaining > 0;
        return (s.slotsRemaining ?? 0) > 0;
      }),
    [
      playerChar?.spellcasting,
      playerChar?.spellSlotsUsed,
      playerChar?.permanent?.spell_mechanics,
      playerChar?.preparedSpells,
      freeCastContext,
    ]
  );

  const getActiveRoomId = useCallback(
    () => (gameSessionId ? 'game' : activeChatRoomId || 'main'),
    [activeChatRoomId, gameSessionId]
  );

  const applyShortcutResult = useCallback(
    (result: SlashSelectResult) => {
      const roomId = getActiveRoomId();
      if (result.action === 'fill') {
        setInputStates((prev) => ({
          ...prev,
          [roomId]: {
            ...(prev[roomId] ?? defaultInputState),
            message: result.text,
            historyIndex: -1,
          },
        }));
        if (result.done) {
          setTimeout(() => handleSendMessageRef.current(), 0);
        }
      } else if (result.action === 'next_step') {
        setInputStates((prev) => ({
          ...prev,
          [roomId]: {
            ...(prev[roomId] ?? defaultInputState),
            message: result.baseText,
            historyIndex: -1,
          },
        }));
      } else if (result.action === 'clear') {
        // Defensive: trigger functions don't currently emit 'clear' (Cancel
        // sentinels live inside the picker UI and route through
        // `processGameSlashSelect`), but mirror that branch so any future
        // trigger path that returns 'clear' resets the input cleanly.
        setInputStates((prev) => ({
          ...prev,
          [roomId]: {
            ...(prev[roomId] ?? defaultInputState),
            message: '',
            historyIndex: -1,
          },
        }));
      }
      inputRef.current?.focus();
    },
    [getActiveRoomId]
  );

  // Ctrl+1 through Ctrl+0 spell shortcuts (game mode only)
  useEffect(() => {
    if (!gameSessionId || numberedSpells.length === 0) return;

    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return;

      // Map keys: 1-9 → index 0-8, 0 → index 9
      let spellIndex = -1;
      if (e.key >= '1' && e.key <= '9') spellIndex = Number(e.key) - 1;
      else if (e.key === '0') spellIndex = 9;
      else return;

      if (spellIndex >= numberedSpells.length) return;

      e.preventDefault();
      const spell = numberedSpells[spellIndex];
      applyShortcutResult(
        triggerSpellShortcut(spell.name, spell.level, spell.freeCastable === true)
      );
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [applyShortcutResult, gameSessionId, numberedSpells, triggerSpellShortcut]);

  // Populate actionTriggerRef so ActionsSection can trigger spell casting.
  // No cleanup nullification — keep ref hot between re-renders to avoid
  // theoretical race where a click lands between cleanup and re-assignment.
  useEffect(() => {
    if (!gameSessionId) {
      actionTriggerRef.current = null;
      return;
    }

    actionTriggerRef.current = {
      triggerSpellShortcut: (spellName: string, spellLevel: number, freeCast?: boolean) => {
        applyShortcutResult(triggerSpellShortcut(spellName, spellLevel, freeCast));
      },
      triggerWeaponAttackShortcut: (weaponName: string) => {
        applyShortcutResult(triggerWeaponAttackShortcut(weaponName));
      },
      triggerAbilityShortcut: (abilityName, targetType) => {
        applyShortcutResult(triggerAbilityShortcut(abilityName, targetType));
      },
      triggerItemShortcut: (data) => {
        applyShortcutResult(triggerItemShortcut(data));
      },
      triggerCardShortcut: (card, runtime) => {
        applyShortcutResult(triggerCardShortcut(card, runtime));
      },
    };
  }, [
    applyShortcutResult,
    gameSessionId,
    triggerSpellShortcut,
    triggerWeaponAttackShortcut,
    triggerAbilityShortcut,
    triggerItemShortcut,
    triggerCardShortcut,
  ]);

  // Fetch lobby journal when not in a game session and party has session history
  const currentParty = parties.find((p) => p.id === currentPartyId);
  const hasSession =
    !!currentParty?.activeGameSessionId ||
    !!currentParty?.lastSessionEnd ||
    (currentParty?.completedModules?.length ?? 0) > 0;
  const sessionKey =
    currentParty?.activeGameSessionId ??
    (typeof currentParty?.lastSessionEnd?.timestamp === 'string'
      ? currentParty.lastSessionEnd.timestamp
      : null);

  // Re-fetch when session changes (e.g., returning from game)
  useEffect(() => {
    setLobbyJournalFetched(false);
  }, [sessionKey, setLobbyJournalFetched]);

  useEffect(() => {
    if (gameSessionId || lobbyJournalFetched || !hasSession) return;

    let cancelled = false;
    const fetchJournal = async () => {
      setLobbyJournalLoading(true);
      try {
        const result = await apiClient.getJournal();
        if (cancelled) return;
        const raw = result.journal;
        if (
          raw &&
          typeof raw === 'object' &&
          Array.isArray((raw as Record<string, unknown>).events)
        ) {
          setLobbyJournal(raw as unknown as import('@danda/shared').CampaignJournal);
        } else {
          setLobbyJournal(null);
        }
      } catch (err) {
        console.error('Failed to fetch lobby journal:', err);
      } finally {
        if (!cancelled) {
          setLobbyJournalLoading(false);
          setLobbyJournalFetched(true);
        }
      }
    };

    fetchJournal();
    return () => {
      cancelled = true;
    };
  }, [
    gameSessionId,
    lobbyJournalFetched,
    hasSession,
    setLobbyJournal,
    setLobbyJournalLoading,
    setLobbyJournalFetched,
  ]);

  const hasLobbyJournalEntries = (lobbyJournal?.events?.length ?? 0) > 0;

  const updateInputState = useCallback(
    (roomId: string, updater: (state: InputState) => InputState) => {
      setInputStates((prev) => {
        const current = prev[roomId] ?? defaultInputState;
        const next = updater(current);
        if (
          current.message === next.message &&
          current.history === next.history &&
          current.historyIndex === next.historyIndex &&
          current.savedInput === next.savedInput
        ) {
          return prev;
        }
        return { ...prev, [roomId]: next };
      });
    },
    []
  );

  const getInputStateForRoom = useCallback(
    (roomId: string) => getInputState(inputStates, roomId),
    [inputStates]
  );

  const activeInputState = getInputStateForRoom(getActiveRoomId());
  const activeMessage = activeInputState.message;

  // Slash command autocomplete state
  const isTypingSlashCommand = activeMessage.startsWith('/');
  const slashQuery = isTypingSlashCommand ? activeMessage.slice(1) : '';
  const roomType = activeRoom?.type;
  // State-aware filter for Tavian's chat: only surface the shortcuts that
  // make sense in the current lobby state (no char → /create-char, party
  // with just the leader → /invite + /select-adventure, etc.).
  const currentPartyForFilter = currentPartyId
    ? parties.find((p) => p.id === currentPartyId)
    : undefined;
  const partyMembersCount = currentPartyForFilter?.members?.length ?? 0;
  const selectedCharForFilter = selectedCharacterId
    ? characters.find((c) => c.id === selectedCharacterId)
    : undefined;
  const canLevelUp = useMemo(() => {
    if (!selectedCharForFilter?.permanent) return false;
    const lvl = (selectedCharForFilter.permanent.level as number | undefined) ?? 1;
    const xp = (selectedCharForFilter.permanent.xp as number | undefined) ?? 0;
    return xp >= getXpForNextLevel(lvl);
  }, [selectedCharForFilter]);
  const availableCommands = useMemo(() => {
    const baseCommands = getCommandsForRoom(roomType);
    if (roomType !== 'tavian') return baseCommands;
    const hasChar = Boolean(selectedCharacterId);
    const hasParty = Boolean(currentPartyId);
    const partyJustLeader = hasParty && partyMembersCount <= 1;
    return baseCommands.filter((cmd) => {
      switch (cmd.name) {
        case 'create-char':
          return !hasChar;
        case 'create-party':
          return hasChar && !hasParty;
        case 'invite':
          return partyJustLeader;
        case 'select-adventure':
          return partyJustLeader;
        // `/equip` opens an inventory picker; without a character the
        // picker would be empty — mirror mobile's hasChar gate.
        case 'equip':
        case 'duel':
        case 'talk-to':
        case 'aldric':
        case 'kira':
        case 'grolt':
        case 'release-character':
        case 'who-is-here':
          return hasChar;
        case 'disband-party':
        case 'rename-party':
          return hasParty;
        case 'level-up':
          return canLevelUp;
        default:
          return true;
      }
    });
  }, [roomType, selectedCharacterId, currentPartyId, partyMembersCount, canLevelUp]);
  // Picker mode for shortcuts whose second step is a list of choices.
  //   /invite, /duel, /talk-to → NPC picker (Tavian only)
  //   /equip                   → equipable-inventory picker
  //                              (Tavian + inner-voice)
  // When the input matches "/<cmd> <partial>", the autocomplete swaps
  // from the static command list to a list of choices. Selecting one
  // submits via the existing autoExecute path; Tavian's branch rewrites
  // slash → NL before sendTavianMessage, inner-voice ships fillText as
  // a regular chat message. Manual typing still works because the
  // dispatch reads whatever the user supplied.
  const roomPickerMatch = useMemo(() => {
    if (roomType !== 'tavian' && roomType !== 'inner' && roomType !== 'grolt') return null;
    const match = activeMessage.match(/^\/(invite|duel|talk-to|equip|sell)\s+(.*)$/i);
    if (!match) return null;
    const cmd = match[1].toLowerCase();
    // Inner-voice only supports /equip; Grolt only supports /sell. Tavian
    // gets the full menu. Anything else here is a routing bug.
    if (roomType === 'inner' && cmd !== 'equip') return null;
    if (roomType === 'grolt' && cmd !== 'sell') return null;
    if (roomType === 'tavian' && cmd === 'sell') return null;
    // hasChar gate for Grolt /sell — the inventory picker would otherwise
    // render for a no-character user, autoExecute would fire
    // `sendGroltMessage`, and `handleSendMessage`'s GROLT branch silently
    // returns when `selectedCharacterId` is null. The picker selection
    // would vanish with no feedback. Mirrors Tavian's `case 'equip':
    // return hasChar` filter (which gates `/equip` from even appearing
    // in the static menu) and mobile's hasChar gating on Grolt commands.
    if (roomType === 'grolt' && !selectedCharacterId) return null;
    return { cmd, partial: match[2] };
  }, [roomType, activeMessage, selectedCharacterId]);

  const filteredSlashCommands = useMemo(() => {
    if (!isTypingSlashCommand) return [];
    if (roomPickerMatch) {
      const { cmd, partial: partialRaw } = roomPickerMatch;
      // Gate through the same state filter that hides the base entry.
      // Without this, a no-party user could type "/invite " and still
      // get an NPC list even though /invite was hidden from the static
      // menu. The check is skipped when the picker's `cmd` doesn't live in
      // `availableCommands` (Tavian's filter): `/equip` for inner-voice
      // and `/sell` for Grolt each ship with their own static command set,
      // not Tavian's.
      const isNonTavianCmd =
        (roomType === 'inner' && cmd === 'equip') || (roomType === 'grolt' && cmd === 'sell');
      if (!isNonTavianCmd && !availableCommands.some((c) => c.name === cmd)) {
        return [];
      }
      const partial = partialRaw.trim().toLowerCase();
      if (cmd === 'equip' || cmd === 'sell') {
        // Inventory picker. `/equip` filters to equippable items via the
        // canonical `EQUIPPABLE_ITEM_KINDS` set (weapon / armor /
        // wondrous_item). The earlier `weapon_data || armor_data` filter
        // was buggy because the inventory schema has no `armor_data` field
        // — armor lives as flat `item_kind: "armor"` + `armor_type:
        // "shield" | "light" | …`, so the old filter silently dropped
        // every armor and shield. `/sell` lists every item so Grolt can
        // quote any price.
        type InventoryItem = {
          name?: string;
          item_id?: string;
          item_kind?: string;
          armor_type?: string;
          weapon_data?: unknown;
        };
        const inv =
          (selectedCharForFilter?.permanent?.inventory as InventoryItem[] | undefined) ?? [];
        const named = inv.filter(
          (i): i is InventoryItem & { name: string } => typeof i.name === 'string' && !!i.name
        );
        const isEquippable = (i: InventoryItem): boolean =>
          (EQUIPPABLE_ITEM_KINDS as readonly string[]).includes(i.item_kind ?? '');
        const eligible = cmd === 'equip' ? named.filter(isEquippable) : named;
        const matched = partial
          ? eligible.filter((i) => i.name.toLowerCase().startsWith(partial))
          : eligible;
        return matched.map<SlashCommandDefinition>((i) => {
          const kindLower = (i.item_kind ?? '').toLowerCase();
          const isWeapon = kindLower === 'weapon';
          const isArmor = kindLower === 'armor';
          const isShield = isArmor && (i.armor_type ?? '').toLowerCase() === 'shield';
          const isWondrous = kindLower === 'wondrous_item';
          const kind = isWeapon
            ? 'Weapon'
            : isShield
              ? 'Shield'
              : isArmor
                ? 'Armor'
                : isWondrous
                  ? 'Wondrous'
                  : 'Item';
          const emoji = isWeapon
            ? '⚔️'
            : isShield
              ? '🛡️'
              : isArmor
                ? '🥋'
                : isWondrous
                  ? '✨'
                  : '🎒';
          return {
            name: `${cmd} ${i.name}`,
            description: kind,
            emoji,
            autoExecute: true,
            fillText:
              cmd === 'equip' ? `Equip my ${i.name}` : `How much will you give me for ${i.name}?`,
          };
        });
      }
      // Character picker. `/invite` lists other PLAYERS' heroes: a party
      // grows by real friends, and a camp NPC named here only walks into
      // the in-character refusal. `/duel` and `/talk-to` still list camp
      // NPCs, which is exactly who you spar and chat with.
      //
      // `/invite` unions two sources. `lobbyCharacters` covers the players
      // standing in the camp right now. `friends` covers everyone else you
      // know, online or not, because an invite to an absent friend is now a
      // real invite that waits for them rather than a refusal.
      const eligible: InvitePickerRow[] =
        cmd === 'invite'
          ? buildInvitePickerRows(lobbyCharacters, friends, selectedCharacterId)
          : lobbyCharacters.filter((c) => c.type === 'npc' && !c.partyId);
      // Prefix match matches the static command-list filter behavior,
      // so typing in the picker behaves consistently with typing for
      // the main slash menu: no surprising mid-name matches surfacing
      // entries that weren't visible with a shorter query.
      const matched = partial
        ? eligible.filter((c) => c.name.toLowerCase().startsWith(partial))
        : eligible;
      return matched.map<SlashCommandDefinition>((c) => {
        const lvl = c.level ? ` Lv${c.level}` : '';
        const meta = [c.species, c.class].filter(Boolean).join(' ');
        const base = meta ? `${meta}${lvl}` : c.name;
        return {
          name: `${cmd} ${c.name}`,
          // Say plainly that they aren't here, so nobody reads the silence
          // that follows as the invite having failed.
          description: c.away ? `${base} · away` : base,
          emoji: c.avatarEmoji,
          autoExecute: true,
        };
      });
    }
    return filterCommands(availableCommands, slashQuery);
  }, [
    isTypingSlashCommand,
    availableCommands,
    slashQuery,
    roomPickerMatch,
    roomType,
    lobbyCharacters,
    friends,
    selectedCharForFilter,
    selectedCharacterId,
  ]);
  const showSlashMenu = filteredSlashCommands.length > 0;

  // Reset slash command index when filtered list changes
  useEffect(() => {
    setSlashCommandIndex(0);
  }, [filteredSlashCommands.length, slashQuery]);

  // Scroll to bottom on initial page load when messages exist
  // Using a ref to track if initial scroll has happened
  const hasInitialScrolled = useRef(false);
  useEffect(() => {
    if (hasInitialScrolled.current) return;
    if (!activeRoom?.messages.length || !lobbyVirtuosoRef.current) return;

    // Scroll to bottom on first render with messages
    hasInitialScrolled.current = true;
    // Use setTimeout to ensure Virtuoso has fully rendered
    setTimeout(() => {
      lobbyVirtuosoRef.current?.scrollToIndex({
        index: 'LAST',
        align: 'end',
        behavior: 'auto', // Use 'auto' for instant scroll on load
      });
    }, 100);
  }, [activeRoom?.messages.length]);

  // Scroll to bottom when active chat room changes (e.g., returning from a duel
  // restores the room from localStorage — messages already exist so followOutput
  // won't trigger, and the initial scroll already fired).
  const prevRoomIdRef = useRef(activeChatRoomId);
  useEffect(() => {
    if (activeChatRoomId === prevRoomIdRef.current) return;
    prevRoomIdRef.current = activeChatRoomId;

    // Give Virtuoso time to mount/render the new room's message list
    setTimeout(() => {
      lobbyVirtuosoRef.current?.scrollToIndex({
        index: 'LAST',
        align: 'end',
        behavior: 'auto',
      });
    }, 150);
  }, [activeChatRoomId]);

  // Handle message sending
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const roomId = getActiveRoomId();
      const value = e.target.value;
      // Feed text to game slash command state machine when in game mode.
      // A `next_step` result means the filter exactly matched a multi-step
      // command (e.g. `/cast` / `/travel`) and the state machine auto-advanced
      // into its picker — `applyShortcutResult` rewrites the input to the
      // template's base text. We skip the usual `updateInputState` in that
      // branch to avoid a double-write race (two `setInputStates` calls in one
      // tick, where the first relies on batching to be overwritten).
      if (gameSessionId) {
        const result = gameSlashHandleTextChange(value);
        // Forward both `next_step` (multi-step picker opened) AND `fill`
        // (state machine finalized immediately — e.g. exact `/cast`
        // command match where the lone-spell + lone-slot + lone-target
        // chain auto-picks the whole way). Without the `fill` branch,
        // a lone-spell `/cast` typed by the player would leave the
        // composer at `/cast` and the eventual submit would fall
        // through as `player_text`.
        if (result.action === 'next_step' || result.action === 'fill') {
          applyShortcutResult(result);
          return;
        }
      }
      updateInputState(roomId, (state) => ({
        ...state,
        message: value,
        historyIndex: -1,
      }));
    },
    [
      getActiveRoomId,
      updateInputState,
      gameSessionId,
      gameSlashHandleTextChange,
      applyShortcutResult,
    ]
  );

  const handleSendMessage = useCallback(async () => {
    // Access store state at call-time to avoid stale closures and reduce dependencies
    const lobbyState = useLobbyStore.getState();
    const gameState = useGameStore.getState();
    const authState = useAuthStore.getState();

    const {
      activeChatRoomId,
      chatRooms,
      currentPartyId,
      characters,
      selectedCharacterId,
      isOnline: isLobbyOnline,
    } = lobbyState;
    const isGameConnected = gameState.connectionStatus.status === 'connected';
    const { user } = authState;

    const activeRoom = chatRooms.find((room) => room.id === activeChatRoomId);
    // Use gameSessionId to determine if we're in game mode (persists during reconnection)
    const roomId = gameState.gameSessionId ? 'game' : activeChatRoomId || 'main';
    const roomInput = inputStates[roomId] ?? defaultInputState;
    // NL → slash fallback for common end-of-session phrasings. Master Kira's
    // post-duel bubble historically pointed players at `/end-session`; this
    // also accepts the looser "end session" / "end-session" forms.
    const rawMessage = roomInput.message;
    const message =
      roomId === 'game' && /^\s*end[\s-]session\s*$/i.test(rawMessage)
        ? '/end-session'
        : rawMessage;
    // Q17 pacing: the next action waits until this client finished SEEING
    // the round (the engine already waits for all player intents, so the
    // per-client input gate is the whole implementation).
    if (roomId === 'game' && useGameStore.getState().presentationPlaybackActive) {
      toast.info('The round is still playing out');
      return;
    }
    // 3D movement rider (combat-3D Q13): a staged board click submits WITH
    // the typed action as one 5E turn; submitted alone (empty input) it is
    // a move-only turn. Sent as its own message: the engine's collector
    // folds same-round intents via the disjoint movement slot.
    const stagedMove = roomId === 'game' ? useGameStore.getState().stagedMove : null;
    if (!message.trim() && !stagedMove) return;
    if (stagedMove) {
      gameConnectionManager.sendAction({
        type: 'player_move',
        movement: { path: stagedMove.path, distance_ft: stagedMove.distanceFt },
        scene_id_at_emit: useGameStore.getState().currentScene?.sceneId,
      });
      useGameStore.getState().setStagedMove(null);
      if (!message.trim()) {
        // Move-only turn: nothing else to send.
        useGameStore.setState({ isWaitingForRound: true });
        return;
      }
    }

    // Signal chat surfaces that the local user is submitting an intent —
    // they re-arm auto-follow + scroll all the way down so the optimistic
    // bubble lands in view even if the user had been reading history.
    // Placed AFTER the empty-input guard so fat-fingered Enter doesn't
    // yank a reader. We can't filter locally-echoed vs server-pushed
    // messages by senderId across all rooms (inner-voice / dead-char /
    // no-character paths use auth uid, not character id), so we trigger
    // explicitly at submit time instead.
    useChatScrollKickStore.getState().bump();

    const trimmedMessage = message.trim().toLowerCase();
    const originalMessage = message.trim();

    const maybeHandleSlashCommand = () => {
      const command = parseSlashCommand(trimmedMessage);
      if (command?.name === 'clear') {
        if (roomId === 'game') {
          gameState.clearMessages();
          toast.success('Game chat cleared');
        } else if (activeChatRoomId) {
          lobbyState.clearChatHistory(activeChatRoomId);
          toast.success('Chat cleared');
        }
        updateInputState(roomId, () => defaultInputState);
        inputRef.current?.focus();
        return true;
      }
      if (command?.name === 'start-adventure') {
        if (currentPartyId) {
          websocketService.startGame(currentPartyId);
        } else {
          toast.warning('You must be in a party to start an adventure');
        }
        updateInputState(roomId, () => defaultInputState);
        inputRef.current?.focus();
        return true;
      }
      if (command?.name === 'feedback') {
        useFeedbackStore.getState().openModal();
        updateInputState(roomId, () => defaultInputState);
        inputRef.current?.focus();
        return true;
      }
      return false;
    };

    // Helper to add user message to the active chat room
    const addUserMessage = () => {
      if (activeChatRoomId && activeChatRoomId !== 'game') {
        const selectedChar = characters.find((c) => c.id === selectedCharacterId);

        // Determine sender based on context:
        // - Party chat & char-chat: ALWAYS the player's character (player can't speak directly)
        // - NPC 1:1 chats (tavian/aldric/kira): Character if exists, otherwise player
        const isCharacterOnlyRoom =
          activeRoom?.type === RoomTypeEnum.PARTY || activeChatRoomId.startsWith('char-chat-');

        let senderId: string;
        let senderName: string;
        let avatarUrl: string | undefined;
        let avatarEmoji: string | undefined;

        const isCharDead = selectedChar?.permanent?.status === 'dead';
        const isTavianRoom = activeRoom?.type === RoomTypeEnum.TAVIAN;
        const isInnerVoiceRoom = activeRoom?.type === RoomTypeEnum.INNER;
        const usePlayerIdentity = isInnerVoiceRoom || (isCharDead && isTavianRoom);

        if (selectedChar && !usePlayerIdentity) {
          // Player has a character - use character identity
          // Exceptions: inner voice room (player speaks as themselves) and dead character in Tavian 1:1
          // Avatar fallback chain mirrors the server-side party-chat
          // resolver (chat.rs) so the sender's optimistic bubble shows
          // the same art the rest of the party will receive: avatarUrl
          // → portraitUrl → avatar. Without portraitUrl, PCs whose
          // `session.avatar_url` is still null (but who have a
          // portrait) painted as a name initial on their own bubble
          // even though the server-broadcast event would have surfaced
          // the portrait to everyone else.
          senderId = selectedChar.id;
          senderName = selectedChar.name;
          avatarUrl = selectedChar.avatarUrl || selectedChar.portraitUrl || selectedChar.avatar;
          avatarEmoji = selectedChar.avatarEmoji || selectedChar.permanent?.avatar_emoji;
        } else if (usePlayerIdentity) {
          // Inner voice room or dead character in Tavian - player speaks as themselves
          senderId = user?.uid || 'user';
          senderName = user?.displayName || user?.email?.split('@')[0] || 'You';
          avatarUrl = user?.photoURL || DEFAULT_PLAYER_AVATAR;
          avatarEmoji = undefined;
        } else if (isCharacterOnlyRoom) {
          // Character-only room but no character - this shouldn't happen
          // (player shouldn't be able to access party chat without a character)
          console.warn('[ChatPanel] Attempted to send in character-only room without character');
          return;
        } else {
          // NPC 1:1 chat with no character (e.g., new player talking to Tavian/Aldric)
          senderId = user?.uid || 'user';
          senderName = user?.displayName || user?.email?.split('@')[0] || 'You';
          avatarUrl = user?.photoURL || DEFAULT_PLAYER_AVATAR;
          avatarEmoji = undefined;
        }

        lobbyState.addMessage(activeChatRoomId, {
          id: `user-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          senderId,
          senderName,
          content: originalMessage,
          timestamp: new Date(),
          type: 'user',
          avatar: avatarUrl,
          avatarUrl,
          avatarEmoji,
        });
      }
    };

    // Check for voting commands in party chat (per LOBBY_SYSTEM.md - voting is chat-driven)
    // Use getState() to read latest decision state at send-time, not stale closure capture
    if (activeRoom?.type === RoomTypeEnum.PARTY && currentPartyId) {
      const activeDecision = useLobbyStore.getState().partyDecisions[currentPartyId];

      if (activeDecision && activeDecision.status === DecisionStatusEnum.PENDING) {
        // Parse voting commands: "yes", "y", "no", "n", "aye", "nay"
        const isYes = ['yes', 'y', 'aye', 'yea', 'agree', 'approve'].includes(trimmedMessage);
        const isNo = ['no', 'n', 'nay', 'disagree', 'reject'].includes(trimmedMessage);

        if (isYes || isNo) {
          // Block "yes" votes on game-start decisions when PM balance is insufficient
          if (isYes && ['start-game', 'continue-game'].includes(activeDecision.type)) {
            try {
              const { pmTokenBalance } = await apiClient.getPmBalance();
              if (pmTokenBalance < 1) {
                toast.warning('Out of Polymyth Tokens', {
                  description:
                    'You need at least 1 PMT to start a game. Invite friends to play together for the biggest token boost, or top up in the token store.',
                  action: {
                    label: 'Invite friends',
                    onClick: () => {
                      track('invite_cta_click', {
                        surface: 'party_vote_toast',
                        low_balance: true,
                      });
                      useAccountModalStore.getState().openModal('invite');
                    },
                  },
                  cancel: {
                    label: 'Buy Tokens',
                    onClick: () => navigate('/buy-tokens'),
                  },
                });
                updateInputState(roomId, () => defaultInputState);
                inputRef.current?.focus();
                return;
              }
            } catch {
              // Allow vote if balance check fails — backend will enforce
            }
          }
          // Send vote via WebSocket
          websocketService.voteOnDecision(activeDecision.id, isYes ? 'yes' : 'no');
          updateInputState(roomId, () => defaultInputState);
          inputRef.current?.focus();
          return; // Don't send as regular chat message
        }
      }
    }

    if (maybeHandleSlashCommand()) {
      return;
    }

    // Low-balance gate — short-circuit any LLM-spending send when the
    // cached PM balance is below MIN_PM_BALANCE. Renders the player's
    // optimistic echo + a Tavian-voiced "Buy Tokens" gate bubble in the
    // SAME room the user typed in, then bails before the WS frame.
    //
    // Server-side per-message pre-check (lobby `dispatch_message`) is
    // the safety net if our cached value is stale.
    //
    // `null` (unknown — initial REST hasn't landed yet) is NOT gated to
    // avoid a flash of the bubble during cold start.
    //
    // /end-session bypasses the gate — it's a cleanup command, not an
    // LLM-spending action, and players in the post-duel recap state may
    // legitimately have low PM but still need to return to the lobby.
    // The downstream intercept (~line 963) routes through
    // gameConnectionManager.endSession() without a WS round-trip.
    const isEndSessionEscape = message.trim() === '/end-session';
    if (!isEndSessionEscape) {
      const cachedBalance = usePmBalanceStore.getState().pmBalance;
      if (cachedBalance !== null && cachedBalance < MIN_PM_BALANCE) {
        if (gameState.gameSessionId) {
          // Game-mode echo: `addUserMessage()` is a no-op when
          // `activeChatRoomId === 'game'` (it filters that room out at
          // line 686), so we have to manually append the player's
          // optimistic echo into `gameState` ourselves. Without this
          // the gate bubble lands without the player's typed message
          // above it — feels like the input was eaten.
          const gameCharId = gameState.playerCharacterId;
          const gameChar = gameCharId
            ? gameState.partyMembers.find((c) => c.charId === gameCharId)
            : undefined;
          const selectedChar = gameChar || characters.find((c) => c.id === selectedCharacterId);
          const senderName =
            gameChar?.name ||
            selectedChar?.name ||
            user?.displayName ||
            user?.email?.split('@')[0] ||
            'You';
          const echoAvatarUrl =
            gameChar?.avatarUrl ||
            selectedChar?.avatarUrl ||
            (selectedChar && 'avatar' in selectedChar ? selectedChar.avatar : undefined) ||
            user?.photoURL ||
            DEFAULT_PLAYER_AVATAR;
          gameState.addMessage({
            id: `user-gate-echo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            senderId:
              gameCharId ||
              (selectedChar && 'id' in selectedChar ? selectedChar.id : undefined) ||
              user?.uid ||
              'user',
            senderName,
            content: originalMessage,
            timestamp: new Date(),
            type: 'user',
            avatarUrl: echoAvatarUrl,
            // Mirror the production send-path fallback chain (line 925-928)
            // so the gate echo renders the same emoji badge the real-send
            // echo would — important for NPC/companion senders whose
            // primary avatar is emoji rather than image.
            avatarEmoji:
              gameChar?.avatarEmoji ||
              selectedChar?.avatarEmoji ||
              selectedChar?.permanent?.avatar_emoji,
          });
          gameState.addMessage({
            id: `gate-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            senderId: 'tavian',
            senderName: 'Tavian',
            content: LOW_BALANCE_GATE_TEXT,
            timestamp: new Date(),
            type: 'token-gate',
            avatarUrl: getStaffAvatarUrl('tavian-avatar.webp'),
            meta: {
              action_kind: 'buyTokens',
              action_label: LOW_BALANCE_GATE_BUTTON_LABEL,
            },
          });
        } else if (activeChatRoomId) {
          // Lobby path: `addUserMessage()` works for any non-'game' room,
          // so reuse it for parity with the existing send-flow.
          addUserMessage();
          lobbyState.addMessage(activeChatRoomId, {
            id: `gate-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            senderId: 'tavian',
            senderName: 'Tavian',
            content: LOW_BALANCE_GATE_TEXT,
            timestamp: new Date(),
            type: 'token-gate',
            avatarUrl: getStaffAvatarUrl('tavian-avatar.webp'),
            meta: {
              action_kind: 'buyTokens',
              action_label: LOW_BALANCE_GATE_BUTTON_LABEL,
            },
          });
        }
        updateInputState(roomId, () => defaultInputState);
        inputRef.current?.focus();
        return;
      }
    }

    // If we have a game session, send to game (use gameSessionId, not activeChatRoomId)
    if (gameState.gameSessionId) {
      if (!isGameConnected) {
        toast.warning('Not connected to game', {
          description: 'Reconnecting to game session...',
        });
        return;
      }

      // Submit-time finalizer — returns a typed Intent envelope for pure menu
      // picks (caller ships `player_intent`, backend skips the LLM agent) or
      // `built: null` for free-text deviations (caller ships `player_text`,
      // backend routes through the LLM agent). See
      // `useGameSlashCommands.finalizeForSubmit` for the rules. The envelope
      // carries the action_slot so a Flurry-of-Blows click lands as
      // `bonus_action` and a `/cast Shield` lands as `reaction`.
      const finalized = finalizeForSubmitRef.current(message.trim());
      const sendText = finalized.sendText;
      const sendBuilt = finalized.built;
      const sendIntent = sendBuilt?.intent ?? null;

      // Add user's message to game chat immediately (optimistic UI update)
      // In game mode, look up character from gameStore.partyMembers using playerCharacterId
      const gameCharId = gameState.playerCharacterId;
      const gameChar = gameCharId
        ? gameState.partyMembers.find((c) => c.charId === gameCharId)
        : undefined;
      // Fallback to lobby character if not found in game state
      const selectedChar = gameChar || characters.find((c) => c.id === selectedCharacterId);
      const senderName =
        gameChar?.name ||
        selectedChar?.name ||
        user?.displayName ||
        user?.email?.split('@')[0] ||
        'You';
      const avatarUrl =
        gameChar?.avatarUrl ||
        selectedChar?.avatarUrl ||
        (selectedChar && 'avatar' in selectedChar ? selectedChar.avatar : undefined) ||
        user?.photoURL ||
        DEFAULT_PLAYER_AVATAR;
      gameState.addMessage({
        id: `user-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        senderId:
          gameCharId ||
          (selectedChar && 'id' in selectedChar ? selectedChar.id : undefined) ||
          user?.uid ||
          'user',
        senderName,
        content: sendText,
        timestamp: new Date(),
        type: 'user',
        avatarUrl,
        avatarEmoji:
          gameChar?.avatarEmoji ||
          selectedChar?.avatarEmoji ||
          selectedChar?.permanent?.avatar_emoji,
      });

      // /end-session bypasses enricher+round and uses the same direct shutdown
      // frame the FE idle-timeout sends — round-pipeline path has bitten us.
      const isEndSessionSlash =
        sendIntent?.intent_type === 'propose_decision' &&
        sendIntent.decision_type === 'end_session';
      if (isEndSessionSlash) {
        // Post-duel recap state: the server has already ended the session and
        // the WS is closed — sending `end_session` would queue forever.
        //
        // Three duel-end paths trigger client-side nav:
        //   1. Recap delivered (sessionEndSummary set) — normal exit.
        //   2. Status flipped to ending/ended — non-duel path or fallback.
        //   3. LLM-gen window: Master Kira's placeholder bubble is in chat
        //      (added on duel session_ending, cleared on session_ended). Its
        //      presence positively proves we're inside the recap LLM call
        //      for the *current* session — unlike duel-portraits localStorage,
        //      which can be stale across sessions and would mis-trigger
        //      client-side bail-out inside a regular adventure.
        const { sessionEndSummary, connectionStatus, messages } = useGameStore.getState();
        const recapShowing = sessionEndSummary !== null;
        const dead = connectionStatus.status === 'ending' || connectionStatus.status === 'ended';
        const inDuelGenWindow = messages.some((m) => m.id === DUEL_COACH_PLACEHOLDER_ID);
        if (recapShowing || dead || inDuelGenWindow) {
          clearGameSessionState('all');
          gameConnectionManager.endSession();
          navigate('/lobby');
        } else {
          gameConnectionManager.sendAction({
            type: 'end_session',
            reason: 'slash_command',
          });
        }
      } else if (sendBuilt) {
        // #214: Optimistic counter decrement BEFORE WS send. Drops the matching
        // counter (Cure Wounds 1/1 → 0/1, Flurry 3/3 → 2/3, sorcery points,
        // item charges, etc.) immediately so the player gets feedback on click
        // instead of waiting for the round result. Snapshot precedence in
        // `syncPlayerCharData` reconciles on rejection (counter rolls back)
        // or confirms it on success.
        if (gameCharId) {
          if (sendBuilt.optimistic) {
            gameState.optimisticallyDecrementResource(
              gameCharId,
              sendBuilt.optimistic.requirement,
              sendBuilt.optimistic.cardLabel
            );
          }
          if (sendBuilt.itemCharge) {
            // Snapshot before the spend, the same way a resource decrement
            // does. `buildOptimistic` skips `charge`, so this path never went
            // through `optimisticallyDecrementResource` and a standalone
            // `intent_rejected` had nothing to roll back — the charge stayed
            // spent until the next authoritative snapshot.
            gameState.captureOptimisticRestore(gameCharId);
            // Charge-based magic items (Wand of Magic Missiles, etc.) — the
            // card carries the item_id + count. Decrement the item's
            // `charges` field directly so the panel reflects the spend on
            // click. Mirror into permanent.magic_item_mechanics because
            // CharDetailsPanel's Actions section reads charges from there;
            // skipping the mirror would leave the action card showing a
            // stale count until the WS round result lands.
            const { item_id, count } = sendBuilt.itemCharge;
            useGameStore.setState((prev) => {
              const updateChar = (char: GameCharacterInfo): GameCharacterInfo => {
                if (char.charId !== gameCharId) return char;
                const equipment = (char.equipment ?? []).map((e) => {
                  if (e.item_id !== item_id) return e;
                  const cur = typeof e.charges === 'number' ? e.charges : 0;
                  return { ...e, charges: Math.max(0, cur - count) };
                });
                let permanent = char.permanent;
                if (permanent?.magic_item_mechanics) {
                  const mechanics = permanent.magic_item_mechanics.map((m) => {
                    if ((m.item_id as string | undefined) !== item_id) return m;
                    const cur = typeof m.charges === 'number' ? m.charges : 0;
                    return { ...m, charges: Math.max(0, cur - count) };
                  });
                  permanent = { ...permanent, magic_item_mechanics: mechanics };
                }
                return { ...char, equipment, permanent };
              };
              const partyMembers = prev.partyMembers.map(updateChar);
              const sceneCharacters = prev.sceneCharacters.map(updateChar);
              const partyMembersMap = { ...prev.partyMembersMap };
              if (partyMembersMap[gameCharId]) {
                partyMembersMap[gameCharId] = updateChar(partyMembersMap[gameCharId]);
              }
              return { partyMembers, sceneCharacters, partyMembersMap };
            });
          }
        }
        // Typed Intent — backend skips the LLM agent and runs through
        // `enrich_intent` directly. The envelope carries `action_slot` and
        // optional `turn_flags` (currently only `reckless_attack`) so a
        // Flurry-of-Blows click lands as bonus_action and a Reckless Attack
        // card click ships the flag without burning the action slot.
        gameConnectionManager.sendAction({
          type: 'player_intent',
          intent: sendBuilt.intent,
          action_slot: sendBuilt.action_slot,
          turn_flags: sendBuilt.turn_flags,
          scene_id_at_emit: currentScene?.sceneId,
        });
      } else if (sendText.length > 0) {
        // Free text (typed prose, or slash command with body deviation) —
        // backend routes through the LLM intent agent.
        gameConnectionManager.sendAction({
          type: 'player_text',
          text: sendText,
          scene_id_at_emit: currentScene?.sceneId,
        });
      }

      // Show thinking indicator while round executes (cleared by narrative_preview/round_result)
      useGameStore.setState({ isWaitingForRound: true });
      // Add to input history and clear input
      updateInputState(roomId, (state) => {
        const trimmed = message.trim();
        const shouldAppend =
          trimmed &&
          (state.history.length === 0 || state.history[state.history.length - 1] !== trimmed);
        const nextHistory = shouldAppend ? [...state.history, trimmed] : state.history;
        return {
          ...defaultInputState,
          history: nextHistory,
        };
      });
      // Reset game slash command state after sending
      gameSlashReset();
      inputRef.current?.focus();
      return;
    }

    if (activeRoom?.type === RoomTypeEnum.ALDRIC) {
      if (!isLobbyOnline) {
        toast.warning('You are offline', {
          description: 'Message will send when the lobby reconnects.',
        });
      }
      addUserMessage();
      // Set waiting state BEFORE sending for optimistic typing indicator
      lobbyState.setWaitingForResponse(true);
      websocketService.sendAldricMessage(message.trim());
    } else if (activeRoom?.type === RoomTypeEnum.KIRA) {
      if (!isLobbyOnline) {
        toast.warning('You are offline', {
          description: 'Message will send when the lobby reconnects.',
        });
      }
      addUserMessage();
      // Set waiting state BEFORE sending for optimistic typing indicator
      lobbyState.setWaitingForResponse(true);
      websocketService.sendKiraMessage(message.trim());
    } else if (activeRoom?.type === RoomTypeEnum.GROLT) {
      if (!isLobbyOnline) {
        toast.warning('You are offline', {
          description: 'Message will send when the lobby reconnects.',
        });
      }
      const charId = lobbyState.selectedCharacterId;
      if (!charId) return;
      addUserMessage();
      lobbyState.setWaitingForResponse(true);
      websocketService.sendGroltMessage(charId, message.trim());
    } else if (activeRoom?.type === RoomTypeEnum.TAVIAN) {
      if (!isLobbyOnline) {
        toast.warning('You are offline', {
          description: 'Message will send when the lobby reconnects.',
        });
      }
      // All messages to Tavian go through the WebSocket
      // Tavian AI decides when to redirect to Aldric/Kira via tools
      addUserMessage();
      // Set waiting state BEFORE sending for optimistic typing indicator
      lobbyState.setWaitingForResponse(true);
      // Lobby slash commands surface in Tavian's autocomplete as shortcuts.
      // Tavian's agent doesn't parse slash syntax — it parses natural-language
      // requests and tool-calls the matching action — so rewrite each
      // recognised slash form into the phrase Tavian's prompt is trained on
      // before forwarding. Visibility/state-gating lives in availableCommands.
      const rawText = message.trim();
      const lower = rawText.toLowerCase();
      const argAfter = (cmd: string) => rawText.slice(cmd.length).trim();
      let llmText = rawText;
      if (lower === '/create-char') {
        llmText = "I'd like to create a new character";
      } else if (lower === '/create-party') {
        llmText = "I'd like to create a new party";
      } else if (lower === '/select-adventure') {
        llmText = 'What adventures can we play?';
      } else if (lower === '/aldric') {
        llmText = 'Take me to Aldric';
      } else if (lower === '/kira') {
        llmText = 'Take me to Master Kira';
      } else if (lower === '/grolt') {
        llmText = 'Take me to Grolt';
      } else if (lower === '/help') {
        llmText = 'What can I do here?';
      } else if (lower === '/release-character') {
        llmText = "I'd like to release my character";
      } else if (lower === '/disband-party') {
        llmText = "I'd like to disband my party";
      } else if (lower === '/level-up') {
        llmText = "I'd like to level up";
      } else if (lower === '/who-is-here') {
        llmText = "Who's here at camp?";
      } else if (lower === '/rename-party') {
        llmText = 'Help me rename the party';
      } else if (lower.startsWith('/talk-to')) {
        const arg = argAfter('/talk-to');
        llmText = arg ? `Take me to ${arg}` : 'Who can I talk to?';
      } else if (lower.startsWith('/invite')) {
        const arg = argAfter('/invite');
        llmText = arg ? `Invite ${arg} to the party` : 'Who can I invite to the party?';
      } else if (lower.startsWith('/duel')) {
        const arg = argAfter('/duel');
        llmText = arg ? `Challenge ${arg} to a practice duel` : 'Help me set up a practice duel';
      } else if (lower.startsWith('/equip')) {
        const arg = argAfter('/equip');
        llmText = arg ? `Equip my ${arg}` : 'What can I equip?';
      }
      websocketService.sendTavianMessage(llmText);
    } else if (activeRoom?.type === 'private') {
      // Character-to-character DM - handle commands
      const lowerCaseMessage = trimmedMessage;

      // Handle /invite command: "/invite [character name]"
      // Party invites are Tavian's job, not the NPC's own roleplay chat:
      // NPCs themselves say as much in character (e.g. Mace: "you gotta send
      // a formal invite through tavian first"). Nothing on the backend parses
      // /invite out of a character-chat message (bug_lobby-invite-slash-command-silent-noop.md:
      // it used to be forwarded as a literal string via sendChatMessage and
      // silently dropped). Redirect through Tavian instead, the same
      // rewrite-to-natural-language path the 'tavian' room branch above uses.
      if (lowerCaseMessage.startsWith('/invite ')) {
        const characterName = message.trim().substring(8).trim();
        if (characterName) {
          if (!isLobbyOnline) {
            toast.warning('You are offline', {
              description: 'Message will send when the lobby reconnects.',
            });
          }
          lobbyState.setWaitingForResponse(true);
          websocketService.sendTavianMessage(`Invite ${characterName} to the party`);
          // Switch to TAVIAN specifically, not just the 'main' room: the
          // reply handlers (npc-events.ts) gate on `mainChatNpc` matching the
          // responding NPC, so if the player was last mid-conversation with
          // Aldric/Kira/Grolt, a bare room switch leaves `mainChatNpc`
          // pointed at that NPC and Tavian's invite response gets silently
          // dropped (never displayed). `returnToTavian()` sets both.
          lobbyState.returnToTavian();
        }
        updateInputState(roomId, () => defaultInputState);
        inputRef.current?.focus();
        return;
      }

      // Handle /leave command
      if (lowerCaseMessage === '/leave') {
        // Leave the DM chat room
        lobbyState.removeChatRoom(activeChatRoomId);
        lobbyState.switchChatRoom('main'); // Return to main NPC chat
        updateInputState(roomId, () => defaultInputState);
        inputRef.current?.focus();
        return;
      }

      // Handle /duel command — challenge NPC to a training duel
      if (lowerCaseMessage === '/duel') {
        if (!selectedCharacterId) {
          toast.warning('You need a character to challenge someone to a duel');
          updateInputState(roomId, () => defaultInputState);
          inputRef.current?.focus();
          return;
        }
        const npcCharId = activeRoom.participants.find((id) => id !== selectedCharacterId);
        if (npcCharId) {
          // Verify the opponent is an NPC — duels are only available against NPCs
          const lobbyState = useLobbyStore.getState();
          const opponentChar = lobbyState.lobbyCharacters.find((c) => c.id === npcCharId);
          if (!opponentChar || opponentChar.type !== 'npc') {
            toast.warning('Duels are only available against NPC opponents');
            updateInputState(roomId, () => defaultInputState);
            inputRef.current?.focus();
            return;
          }
          // Store duel participant portraits for the loading screen.
          // PCs aren't broadcast in lobby:characters, so the player's own char
          // lives in `state.characters` (own list). NPCs live in `lobbyCharacters`.
          // Persist charIds so the loader can re-resolve fresh URLs from the
          // store when localStorage URLs are stale or missing.
          const playerChar = lobbyState.characters.find((c) => c.id === selectedCharacterId);
          if (playerChar || opponentChar) {
            const playerImageUrl =
              playerChar?.portraitUrl ||
              playerChar?.session?.portrait_url ||
              playerChar?.avatarUrl ||
              playerChar?.avatar;
            const opponentImageUrl =
              opponentChar?.portraitUrl || opponentChar?.avatarUrl || opponentChar?.avatar;
            const prewrite = {
              player: {
                charId: selectedCharacterId,
                name: playerChar?.name,
                imageUrl: playerImageUrl,
              },
              opponent: {
                charId: npcCharId,
                name: opponentChar?.name,
                imageUrl: opponentImageUrl,
              },
            };
            console.log('[duel-loader] ChatPanel /duel pre-write', {
              prewrite,
              playerCharFields: playerChar
                ? {
                    portraitUrl: playerChar.portraitUrl,
                    sessionPortrait: playerChar.session?.portrait_url,
                    avatarUrl: playerChar.avatarUrl,
                    avatar: playerChar.avatar,
                  }
                : null,
              opponentCharFields: {
                portraitUrl: opponentChar.portraitUrl,
                avatarUrl: opponentChar.avatarUrl,
                avatar: opponentChar.avatar,
              },
            });
            localStorage.setItem('game-session-duel-portraits', JSON.stringify(prewrite));
          }
          // `/duel` here runs inside `activeRoom` (the opponent's 1:1
          // char-chat — the npcCharId comes from `activeRoom.participants`).
          // Set `initiatedFromChat: true` so the backend routes the
          // rejection through this NPC's chat LLM in-voice. Other duel
          // entry points (if any are added later) must pass `false`.
          websocketService.startTrainingDuel(npcCharId, true);
          toast.info('Challenging to a practice duel...');
        } else {
          toast.warning('No opponent found in this chat');
        }
        updateInputState(roomId, () => defaultInputState);
        inputRef.current?.focus();
        return;
      }

      // Handle /form-party command: "/form-party [party name]"
      if (lowerCaseMessage.startsWith('/form-party ')) {
        const partyName = message.trim().substring(12).trim();
        if (partyName) {
          // Send to Tavian via 1:1 chat to create party
          // Note: This should ideally be done in party chat, but for now route through Tavian
          lobbyState.setWaitingForResponse(true);
          if (!isLobbyOnline) {
            toast.warning('You are offline', {
              description: 'Message will send when the lobby reconnects.',
            });
          }
          websocketService.sendTavianMessage(
            `I'd like to form a party called "${partyName}" with the members of this chat.`
          );
          updateInputState(roomId, () => defaultInputState);
          inputRef.current?.focus();
          return;
        }
      }

      // Regular DM message (includes char-chat rooms from Tavian's start_char_chat)
      addUserMessage();
      // For char-chat rooms, set waiting state for AI response
      if (activeChatRoomId.startsWith('char-chat-')) {
        lobbyState.setWaitingForResponse(true);
      }
      if (!isLobbyOnline) {
        toast.warning('You are offline', {
          description: 'Message will send when the lobby reconnects.',
        });
      }
      websocketService.sendChatMessage(activeChatRoomId, message.trim());
    } else if (activeRoom?.type === RoomTypeEnum.INNER) {
      addUserMessage();
      lobbyState.setWaitingForResponse(true);
      if (!isLobbyOnline) {
        toast.warning('You are offline', {
          description: 'Message will send when the lobby reconnects.',
        });
      }
      websocketService.sendChatMessage(activeChatRoomId, message.trim());
    } else if (activeRoom) {
      // Send via Lobby WebSocket for other chat rooms (party chat, etc.)
      if (!isLobbyOnline) {
        toast.warning('You are offline', {
          description: 'Message will send when the lobby reconnects.',
        });
      }
      // Add user message to chat for party chat (Tavian responds in party chat)
      if (activeRoom.type === RoomTypeEnum.PARTY) {
        addUserMessage();
        lobbyState.setWaitingForResponse(true);
      }
      websocketService.sendChatMessage(activeChatRoomId, message.trim());
    }

    // Add to input history for arrow-up recall
    updateInputState(roomId, (state) => {
      const trimmed = message.trim();
      const shouldAppend =
        trimmed &&
        (state.history.length === 0 || state.history[state.history.length - 1] !== trimmed);
      const nextHistory = shouldAppend ? [...state.history, trimmed] : state.history;
      return {
        ...defaultInputState,
        history: nextHistory,
      };
    });
    inputRef.current?.focus();
  }, [inputStates, updateInputState, gameSlashReset, navigate, currentScene?.sceneId]);

  // Keep ref in sync so deferred calls (setTimeout) always use the latest closure
  handleSendMessageRef.current = handleSendMessage;

  // Handle slash command selection - must be after handleSendMessage
  const handleSlashCommandSelect = useCallback(
    (command: SlashCommandDefinition) => {
      const roomId = getActiveRoomId();

      // Tavian's /duel opens an NPC picker (filteredSlashCommands picks up
      // the "/duel " prefix and surfaces present NPCs). Override the static
      // autoExecute so we pre-fill instead of immediately submitting bare
      // "/duel" — private-DM /duel still autoExecutes because that branch
      // uses room participants, not a typed name.
      const isTavianDuelPicker =
        command.name === 'duel' && activeRoom?.type === RoomTypeEnum.TAVIAN;

      if (command.hasArg || isTavianDuelPicker) {
        // Fill with command and space for argument / picker stage
        updateInputState(roomId, (state) => ({
          ...state,
          message: `/${command.name} `,
          historyIndex: -1,
        }));
      } else if (command.autoExecute) {
        // Fire immediately — send fillText if defined (guided prompt),
        // otherwise the bare /command.
        const payload = command.fillText ?? `/${command.name}`;
        updateInputState(roomId, (state) => ({
          ...state,
          message: payload,
          historyIndex: -1,
        }));
        setTimeout(() => {
          handleSendMessageRef.current();
        }, 0);
      } else if (command.fillText) {
        // Replace input with fill text the user is expected to complete
        // (e.g. "Tavian: ").
        updateInputState(roomId, (state) => ({
          ...state,
          message: command.fillText!,
          historyIndex: -1,
        }));
      } else {
        // Execute command immediately by filling and triggering send
        updateInputState(roomId, (state) => ({
          ...state,
          message: `/${command.name}`,
          historyIndex: -1,
        }));
        // Trigger send after state update via ref to avoid stale closure
        // (setTimeout captures the current handleSendMessage which has old inputStates)
        setTimeout(() => {
          handleSendMessageRef.current();
        }, 0);
      }
      inputRef.current?.focus();
    },
    [getActiveRoomId, updateInputState, activeRoom?.type]
  );

  const handleSlashCommandClose = useCallback(() => {
    // Clear the slash to close menu
    const roomId = getActiveRoomId();
    updateInputState(roomId, (state) => ({
      ...state,
      message: '',
      historyIndex: -1,
    }));
    inputRef.current?.focus();
  }, [getActiveRoomId, updateInputState]);

  // Pool-spend custom-input Dialog state. Surfaced when the wizard's
  // "Other amount…" row is picked; the Dialog returns the typed amount
  // and we re-enter `handleSelect` with a synthesized item.
  const [poolInputDialog, setPoolInputDialog] = useState<{
    poolMax: number;
  } | null>(null);

  // Handle game slash command selection result — updates input and may auto-send
  const processGameSlashSelect = useCallback(
    (item: SlashCommandDefinition) => {
      const roomId = getActiveRoomId();
      const result = gameSlashHandleSelect(item);

      if (result.action === 'fill') {
        // Set input to the filled text
        updateInputState(roomId, (s) => ({
          ...s,
          message: result.text,
          historyIndex: -1,
        }));
        // If done and not ending with free text, auto-send
        if (result.done) {
          setTimeout(() => {
            handleSendMessageRef.current();
          }, 0);
        }
        inputRef.current?.focus();
      } else if (result.action === 'next_step') {
        // Set input to the base text for the next stage
        updateInputState(roomId, (s) => ({
          ...s,
          message: result.baseText,
          historyIndex: -1,
        }));
        inputRef.current?.focus();
      } else if (result.action === 'request_pool_input') {
        // Open the styled numeric Dialog. The wizard stays parked on the
        // pool_spend step until the Dialog returns; cancel leaves the
        // user in the same step (they can pick another row).
        setPoolInputDialog({ poolMax: result.poolMax });
      } else if (result.action === 'clear') {
        // Per-spell shortcut Cancel: hook has already reset the wizard to
        // idle. Drop the chat-input message so the picker closes cleanly.
        updateInputState(roomId, (s) => ({ ...s, message: '', historyIndex: -1 }));
        inputRef.current?.focus();
      }
      // 'noop' — do nothing
    },
    [getActiveRoomId, gameSlashHandleSelect, updateInputState]
  );

  // Handle game slash Esc — go back one step
  const processGameSlashBack = useCallback(() => {
    const roomId = getActiveRoomId();
    const result = gameSlashHandleBack();

    if (result.action === 'clear') {
      updateInputState(roomId, (s) => ({ ...s, message: '', historyIndex: -1 }));
    } else if (result.action === 'back_to_commands' || result.action === 'back_to_step') {
      updateInputState(roomId, (s) => ({ ...s, message: result.text, historyIndex: -1 }));
    }
    inputRef.current?.focus();
  }, [getActiveRoomId, gameSlashHandleBack, updateInputState]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      const roomId = getActiveRoomId();
      const state = inputStates[roomId] ?? defaultInputState;

      // Game mode: handle game slash command menu navigation
      if (gameSessionId && gameSlash.showMenu) {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          gameSlashSetSelectedIndex(
            gameSlash.selectedIndex > 0
              ? gameSlash.selectedIndex - 1
              : gameSlash.menuItems.length - 1
          );
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          gameSlashSetSelectedIndex(
            gameSlash.selectedIndex < gameSlash.menuItems.length - 1
              ? gameSlash.selectedIndex + 1
              : 0
          );
          return;
        }
        if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
          e.preventDefault();
          const selectedItem = gameSlash.menuItems[gameSlash.selectedIndex];
          if (selectedItem) {
            processGameSlashSelect(selectedItem);
          }
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          // Also mark the native event so window-level handlers (e.g., message queue skip) don't fire
          e.nativeEvent.stopImmediatePropagation();
          processGameSlashBack();
          return;
        }
      }

      // Lobby mode: handle lobby slash command menu navigation
      if (showSlashMenu && !gameSessionId) {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSlashCommandIndex((prev) => (prev > 0 ? prev - 1 : filteredSlashCommands.length - 1));
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSlashCommandIndex((prev) => (prev < filteredSlashCommands.length - 1 ? prev + 1 : 0));
          return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          const selectedCommand = filteredSlashCommands[slashCommandIndex];
          if (selectedCommand) {
            handleSlashCommandSelect(selectedCommand);
          }
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          handleSlashCommandClose();
          return;
        }
        if (e.key === 'Tab') {
          e.preventDefault();
          const selectedCommand = filteredSlashCommands[slashCommandIndex];
          if (selectedCommand) {
            handleSlashCommandSelect(selectedCommand);
          }
          return;
        }
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      } else if (e.key === 'ArrowUp') {
        // Navigate back in message history (per chat room)
        // History contains previously sent messages for easy resending
        if (state.history.length === 0) return;
        e.preventDefault();

        if (state.historyIndex === -1) {
          // Starting navigation - save current unsent input and show last message
          const newIndex = state.history.length - 1;
          updateInputState(roomId, (s) => ({
            ...s,
            savedInput: s.message,
            historyIndex: newIndex,
            message: s.history[newIndex],
          }));
        } else if (state.historyIndex > 0) {
          // Go further back in history
          const newIndex = state.historyIndex - 1;
          updateInputState(roomId, (s) => ({
            ...s,
            historyIndex: newIndex,
            message: s.history[newIndex],
          }));
        }
        // If historyIndex === 0, we're at the first message - stay there
      } else if (e.key === 'ArrowDown') {
        // Navigate forward in message history
        if (state.historyIndex === -1) return; // Not in history navigation
        e.preventDefault();

        if (state.historyIndex < state.history.length - 1) {
          // Go forward in history
          const newIndex = state.historyIndex + 1;
          updateInputState(roomId, (s) => ({
            ...s,
            historyIndex: newIndex,
            message: s.history[newIndex],
          }));
        } else {
          // At the end of history - return to current (unsent) input
          updateInputState(roomId, (s) => ({
            ...s,
            historyIndex: -1,
            message: s.savedInput,
          }));
        }
      }
    },
    [
      getActiveRoomId,
      handleSendMessage,
      inputStates,
      updateInputState,
      showSlashMenu,
      filteredSlashCommands,
      slashCommandIndex,
      handleSlashCommandSelect,
      handleSlashCommandClose,
      gameSessionId,
      gameSlash,
      gameSlashSetSelectedIndex,
      processGameSlashSelect,
      processGameSlashBack,
    ]
  );

  // Helper to look up avatar emoji for a character if not already in message
  const getAvatarEmoji = useCallback(
    (senderId: string, existingEmoji?: string) => {
      if (existingEmoji) return existingEmoji;
      // Look in lobbyCharacters
      const lobbyChar = lobbyCharacters.find((c) => c.id === senderId);
      if (lobbyChar?.avatarEmoji) return lobbyChar.avatarEmoji;
      // Look in party members
      for (const party of parties) {
        const member = party.members?.find((m) => m.id === senderId);
        if (member?.avatarEmoji) return member.avatarEmoji;
      }
      return undefined;
    },
    [lobbyCharacters, parties]
  );

  const getBubbleColor = useCallback(
    (senderId: string, type?: string, senderName?: string) => {
      // Staff NPCs get fixed branded colors
      if (type === RoomTypeEnum.TAVIAN || senderId === RoomTypeEnum.TAVIAN) return '#14B8A6'; // teal-500
      if (type === RoomTypeEnum.ALDRIC || senderId === RoomTypeEnum.ALDRIC) return '#3B82F6'; // blue-500
      if (type === RoomTypeEnum.KIRA || senderId === RoomTypeEnum.KIRA) return '#A855F7'; // purple-500
      if (type === RoomTypeEnum.GROLT || senderId === RoomTypeEnum.GROLT) return '#B45309'; // amber-700

      // Explicit overrides from character data
      const lobbyChar = lobbyCharacters.find((c) => c.id === senderId);
      if (typeof lobbyChar?.chatColor === 'string') return lobbyChar.chatColor;

      const char = characters.find((c) => c.id === senderId);
      if (typeof char?.permanent?.chat_bubble_color === 'string') {
        return char.permanent.chat_bubble_color;
      }

      // All other senders (including players) get a unique deterministic color by id/name
      const seed = senderId || senderName;
      return seed ? pickChatBubbleColor(seed) : undefined;
    },
    [characters, lobbyCharacters]
  );

  // Precompute hero video injection targets (messageId → videoUrl) once per room update.
  // Anchored to the FIRST NPC message in each room so the target is stable —
  // using the LAST message caused ~200px height swings every time the user sent a
  // message (video removed → scroll jump → video re-added on next NPC reply).
  const heroVideoTargets = useMemo(() => {
    const targets = new Map<string, string>();
    const hasMarker = (content?: string) =>
      !!content &&
      (content.includes('[NPC_HERO:') ||
        content.includes('[PARTY_IMAGE:') ||
        content.includes('[MODULE_IMAGE:') ||
        content.includes('[CHAR_PORTRAIT:'));

    for (const room of chatRooms) {
      if (room.messages.length === 0) continue;

      // Lobby main room: pin each NPC's hero video to that NPC's FIRST message.
      // Keying on senderId (not room.type) keeps Tavian's intro video above his
      // greeting even after he redirects to Aldric — the room type flips to
      // 'aldric' but the tavian message stays. Skip per-message when a marker is
      // already baked into the content (e.g. Aldric's char-creation intro at
      // npc-events.ts) so we never double-inject.
      if (room.id === 'main') {
        const seenNpc = new Set<string>();
        for (const msg of room.messages) {
          const npcVideo = NPC_HERO_VIDEOS[msg.senderId];
          if (!npcVideo || seenNpc.has(msg.senderId)) continue;
          seenNpc.add(msg.senderId);
          if (!hasMarker(msg.content)) targets.set(msg.id, npcVideo);
        }
        continue;
      }

      // Check ALL messages for media markers — a [PARTY_IMAGE:] on an earlier message
      // means the room already has a visual anchor, so skip hero video injection.
      const hasMediaMarker = room.messages.some((m) => hasMarker(m.content));
      if (hasMediaMarker) continue;

      // Inner voice rooms: inject character portrait banner on first message
      if (room.type === 'inner' && room.portraitUrl) {
        const firstInnerMsg = room.messages.find((msg) => msg.senderId === 'inner_voice');
        if (firstInnerMsg) {
          targets.set(firstInnerMsg.id, `[PORTRAIT]${room.portraitUrl}`);
        }
        continue;
      }

      // Determine which hero video this room should show
      const heroVideo =
        NPC_HERO_VIDEOS[room.type] ?? (room.type === 'party' ? NPC_HERO_VIDEOS.tavian : undefined);
      if (!heroVideo) continue;

      // Find the FIRST NPC message to anchor the hero video (stable across sends)
      const firstNpcMsg = room.messages.find((msg) =>
        room.type === 'party' ? msg.senderId === 'tavian' : msg.senderId === room.type
      );

      if (firstNpcMsg) {
        targets.set(firstNpcMsg.id, heroVideo);
      }
    }
    return targets;
  }, [chatRooms]);

  // Message item renderer for Virtuoso (lobby messages)
  const renderLobbyMessageItem = useCallback(
    (
      msg: import('@/lib/stores/lobbyStore').ChatMessage,
      _index: number,
      _roomType: string,
      revealProps?: { isQueuedReveal: boolean; onRevealComplete: () => void }
    ) => {
      const heroMedia = heroVideoTargets.get(msg.id);
      let displayContent = msg.content;
      if (heroMedia?.startsWith('[PORTRAIT]')) {
        displayContent = `[CHAR_PORTRAIT:${heroMedia.slice('[PORTRAIT]'.length)}]\n\n${msg.content}`;
      } else if (heroMedia) {
        displayContent = `[NPC_HERO:${heroMedia}]\n\n${msg.content}`;
      }

      // Resolve sender portrait URL — look up live from stores rather than relying
      // on it being baked into the message at construction time (many sites).
      // Staff NPCs (tavian/aldric/kira/grolt) aren't in the character stores, so
      // resolve them via the static asset path helper.
      const staffPortrait = STAFF_PORTRAIT_AVATAR_NAMES[msg.senderId];
      const resolvedSenderPortraitUrl =
        msg.senderPortraitUrl ??
        (staffPortrait ? getStaffPortraitUrl(staffPortrait) : undefined) ??
        characters.find((c) => c.id === msg.senderId)?.portraitUrl ??
        lobbyCharacters.find((c) => c.id === msg.senderId)?.portraitUrl ??
        undefined;

      return (
        <div className="pb-3">
          <ChatMessage
            key={msg.id}
            id={msg.id}
            content={displayContent}
            senderName={msg.senderName}
            timestamp={msg.timestamp}
            type={msg.type}
            container="lobby"
            isStreaming={msg.isStreaming}
            avatar={msg.avatar}
            avatarUrl={msg.avatarUrl || msg.avatar}
            avatarEmoji={getAvatarEmoji(msg.senderId, msg.avatarEmoji)}
            senderPortraitUrl={resolvedSenderPortraitUrl}
            bubbleColor={getBubbleColor(msg.senderId, msg.type, msg.senderName)}
            isQueuedReveal={revealProps?.isQueuedReveal}
            onRevealComplete={revealProps?.onRevealComplete}
            meta={msg.meta}
          />
        </div>
      );
    },
    [heroVideoTargets, getAvatarEmoji, getBubbleColor, characters, lobbyCharacters]
  );

  const handleCloseRoom = useCallback(
    (roomId: string) => {
      if (roomId.startsWith('char-chat-')) {
        const chatSessionId = roomId.replace('char-chat-', '');
        websocketService.closeCharacterChat(chatSessionId);
      }
      removeChatRoom(roomId);
    },
    [removeChatRoom]
  );

  return (
    <Card className={`flex flex-col ${isExpanded ? 'h-full' : 'h-auto'}`}>
      {/* Only show Chat header in lobby - game session uses tabs */}
      {!gameSessionId && (
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="h-4 w-4" />
            Polymyth
          </CardTitle>
        </CardHeader>
      )}
      {/* Game session: minimal header with just collapse button */}
      {gameSessionId && (
        <CardHeader className="pb-1 pt-2 px-3">
          <div className="flex items-center justify-end">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={toggleExpanded}
              aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
      )}
      {isExpanded && (
        <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
          <Tabs
            value={
              gameSessionId
                ? gameTab
                : lobbyTab === 'journal'
                  ? '__journal__'
                  : activeChatRoomId || 'main'
            }
            onValueChange={(v) => {
              if (gameSessionId) {
                setGameTab(v as 'game' | 'journal');
                if (v === 'journal') clearJournalUnread();
              } else if (v === '__journal__') {
                setLobbyTab('journal');
              } else {
                setLobbyTab('chat');
                switchChatRoom(v);
              }
            }}
            className="flex-1 flex flex-col min-h-0"
          >
            {/* Game session: game + journal tabs */}
            {gameSessionId && (
              <TabsList className="mx-4 mb-2 justify-start h-8 bg-transparent p-0 border-b rounded-none">
                <TabsTrigger
                  value="game"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-3 py-1.5 text-sm"
                >
                  {moduleTitle || 'Adventure'}
                  {currentScene?.name && (
                    <span className="text-muted-foreground font-normal">
                      {' '}
                      — {formatSceneName(currentScene.name)}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="journal"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-3 py-1.5 text-sm flex items-center gap-1.5"
                >
                  <ScrollText className="h-3.5 w-3.5" />
                  Journal
                  {unreadJournalCount > 0 && (
                    <span className="ml-1 px-1.5 py-0 text-[10px] rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                      {unreadJournalCount}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
            )}
            {/* Hide lobby chat tabs when in a game session (even if temporarily disconnected) */}
            {!gameSessionId && (
              <ChatRoomTabs rooms={chatRooms} onCloseRoom={handleCloseRoom}>
                {hasLobbyJournalEntries && (
                  <TabsTrigger value="__journal__" className="flex items-center gap-1.5 shrink-0">
                    <ScrollText className="h-3.5 w-3.5" />
                    Journal
                  </TabsTrigger>
                )}
              </ChatRoomTabs>
            )}

            {/* Show GameChatContent if we have a gameSessionId (even if temporarily disconnected) */}
            {gameSessionId && gameTab === 'journal' ? (
              <div className="flex-1 overflow-y-auto min-h-0">
                <JournalTabContent />
              </div>
            ) : gameSessionId ? (
              <GameChatContent
                isActive={true}
                messages={gameMessages}
                virtuosoRef={gameVirtuosoRef}
                inputRef={inputRef}
                inputValue={activeMessage}
                onInputChange={handleInputChange}
                onKeyPress={handleKeyPress}
                onSend={handleSendMessage}
                isConnected={isGameConnected}
                slashCommands={gameSlash.showMenu ? gameSlash.menuItems : undefined}
                slashCommandIndex={gameSlash.selectedIndex}
                onSlashCommandSelect={processGameSlashSelect}
                onSlashCommandClose={gameSlashReset}
                slashStageLabel={gameSlash.stageLabel}
                slashMenuActive={gameSlash.isActive}
                activeDescendantId={
                  gameSlash.showMenu ? slashMenuItemId(gameSlash.selectedIndex) : undefined
                }
              />
            ) : lobbyTab === 'journal' ? (
              <div className="flex-1 overflow-y-auto min-h-0">
                <JournalTabContent journal={lobbyJournal} />
              </div>
            ) : (
              chatRooms.map((room) => {
                const roomInputState = getInputStateForRoom(room.id);
                return (
                  <TabsContent
                    key={room.id}
                    value={room.id}
                    className="flex-1 flex flex-col mx-4 mb-4 mt-0 data-[state=inactive]:hidden h-full"
                    forceMount
                  >
                    <LobbyRoomContent
                      room={room}
                      lobbyVirtuosoRef={lobbyVirtuosoRef}
                      renderMessageItem={renderLobbyMessageItem}
                      isWaitingForResponse={isWaitingForResponse}
                      pendingInvites={pendingInvites}
                      isLobbyOnline={isLobbyOnline}
                      roomInputValue={roomInputState.message}
                      onInputChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      onSend={handleSendMessage}
                      lobbyCharacters={lobbyCharacters}
                      characters={characters}
                      parties={parties}
                      currentPartyId={currentPartyId}
                      selectedCharacterId={selectedCharacterId}
                      partyDecisions={partyDecisions}
                      onDecisionResolved={clearPartyDecision}
                      inputRef={inputRef}
                      isActiveRoom={activeChatRoomId === room.id}
                      isTavianRoom={
                        activeRoom?.type === RoomTypeEnum.TAVIAN && activeRoom.id === room.id
                      }
                      slashCommands={
                        activeChatRoomId === room.id ? filteredSlashCommands : undefined
                      }
                      slashCommandIndex={slashCommandIndex}
                      onSlashCommandSelect={handleSlashCommandSelect}
                      onSlashCommandClose={handleSlashCommandClose}
                    />
                  </TabsContent>
                );
              })
            )}
          </Tabs>
        </CardContent>
      )}
      <PoolSpendInputDialog
        poolMax={poolInputDialog?.poolMax ?? null}
        onConfirm={(amount) => {
          setPoolInputDialog(null);
          // Synthesize the picker row the wizard would have rendered for
          // this discrete amount and feed it back. The wizard is parked
          // on the pool_spend step waiting for a real pick.
          processGameSlashSelect({
            name: `${amount} HP`,
            description: `Spend ${amount} HP`,
            isMenuOption: true,
            sourceId: String(amount),
            meta: { poolSpend: amount },
          });
        }}
        onCancel={() => setPoolInputDialog(null)}
      />
    </Card>
  );
}
